import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { HotkeysProvider } from '@tanstack/react-hotkeys';
import { AppToaster } from './components/ui/AppToaster.jsx';
import { queryClient } from './lib/query-client.js';
import { cn } from './lib/cn.js';

export default function Root() {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  // The office app (/offices/*) carries its own chrome (sidebar shell) —
  // the slim global header only frames the public/onboarding pages.
  const isOfficeApp = location.pathname.startsWith('/offices');

  // The template renders no <html>/<head> shell (data-router SPA mode) —
  // the document title is owned here, reactively with the language.
  useEffect(() => {
    document.title = t('app.name');
  }, [t, i18n.language]);

  function setLang(lng) {
    i18n.changeLanguage(lng);
    document.documentElement.lang = lng;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HotkeysProvider>
      <div className="flex min-h-dvh flex-col bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
        {!isOfficeApp && (
          <header className="flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2">
            <Link to="/" className="text-sm font-semibold">{t('app.name')}</Link>
            <nav aria-label={t('lang.switcher.label')} className="flex gap-1">
              {['fr', 'en'].map((lng) => (
                <button
                  key={lng}
                  type="button"
                  onClick={() => setLang(lng)}
                  aria-pressed={i18n.language.startsWith(lng)}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs font-medium',
                    i18n.language.startsWith(lng)
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                  )}
                >
                  {t(`lang.${lng}`)}
                </button>
              ))}
            </nav>
          </header>
        )}
        <main className="flex flex-1 flex-col">
          <Outlet />
        </main>
      </div>
      {/* Portalled to <body> : stays reachable when the root is inert (MissionPane). */}
      <AppToaster richColors position="top-right" />
      </HotkeysProvider>
    </QueryClientProvider>
  );
}
